import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  distinctUntilChanged,
  exhaustMap,
  filter,
  fromEvent,
  ignoreElements,
  interval,
  map,
  merge,
  of,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';

import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import { NotificationAction } from '@core/models/notifications.model';
import { PlanEditGesture } from '@core/models/plan-edit-gesture.model';
import { GbeErrorParserService } from '@core/services/gbe-error-parser/gbe-error-parser.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { PlanEditSocketService } from '@core/services/plan-edit/plan-edit-socket.service';
import { ReferenceNamesService } from '@core/services/reference-names/reference-names.service';
import { UserActivityService } from '@core/services/user-activity/user-activity.service';
import { selectTimelineVisualization } from '@core/store/data-visualization/data-visualization.selectors';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { withCompanyCleared } from '@core/utils/plan-edit/company-clears';
import {
  PLAN_EDIT_MAX_INTENTS,
  intentsFor,
  touchedRouteUids
} from '@core/utils/plan-edit/intents';
import {
  breachUids,
  planEditHasOwnText,
  planEditMessageKey,
  planEditMessageParams,
  withKnownNames
} from '@core/utils/plan-edit/messages';
import { predict } from '@core/utils/plan-edit/predictions';

import { TimelineRow } from '@features/components/visualization/timeline/models/timeline';

import {
  dropReloadSnapshot,
  reloadSnapshotOf,
  stashReloadSnapshot,
  takeReloadSnapshot
} from './plan-edit-reload';
import { PlanEditSessionActions as Session } from './plan-edit-session.actions';
import { CONNECTION_LOST } from './plan-edit-session.reducer';
import {
  selectCanRedo,
  selectCanUndo,
  selectPlanEditSession,
  selectPlanWatchForView
} from './plan-edit-session.selectors';
import { PlanEditActions, PlanWatchActions } from './plan-edit.actions';
import { PlanEditSessionState } from './plan-edit.state';

/** DL closes a session after 10 idle minutes; a heartbeat every 2 keeps an active one open. */
export const PLAN_EDIT_HEARTBEAT_MS = 120_000;

const PLAN_EDIT_KEY = 'features.plan.edit';

function routesOnScreen(rows: TimelineRow[]): Record<string, UserRouteV1Model> {
  const routes: Record<string, UserRouteV1Model> = {};
  for (const row of rows) {
    if (row.route) routes[row.uid] = row.route;
  }
  return routes;
}

/**
 * Drives this tab's edit session over the socket. Every command carrying a seq takes the session's
 * `nextClientSeq`, and the action reserving it is dispatched only once the command went out: one the
 * socket could not send is a lost connection.
 */
@Injectable()
export class PlanEditSessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly socket = inject(PlanEditSocketService);
  private readonly activity = inject(UserActivityService);
  private readonly translate = inject(TranslateService);
  private readonly notifications = inject(NotificationService);
  private readonly referenceNames = inject(ReferenceNamesService);
  private readonly errorParser = inject(GbeErrorParserService);
  private readonly window = inject(DOCUMENT).defaultView;
  private lastEditedPlanId: string | null = null;

  /**
   * A tab that never watched has no connection yet when Edit is pressed, so the open goes out again
   * on each connection while the session is still opening. Edit pressed again meanwhile sends nothing.
   */
  readonly open$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Session.editRequested),
        exhaustMap(({ planId, paramId }) =>
          merge(
            of(null),
            this.actions$.pipe(ofType(PlanWatchActions.socketConnected))
          ).pipe(
            takeUntil(
              this.store
                .select(selectPlanEditSession)
                .pipe(filter((s) => s.mode !== 'opening' || s.planId !== planId))
            ),
            tap(() => this.socket.openSession(planId, paramId))
          )
        )
      ),
    { dispatch: false }
  );

  readonly gesture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Session.gestureRequested),
      concatLatestFrom(() => [
        this.store.select(selectPlanEditSession),
        this.store.select(selectTimelineVisualization)
      ]),
      filter(([, session]) => session.mode === 'editing'),
      map(([{ gesture }, session, timeline]) => {
        const intents = intentsFor(gesture);
        if (intents.length > PLAN_EDIT_MAX_INTENTS) {
          return Session.gestureRefusedLocally({
            messageKey: 'features.plan.edit.errors.too_many_jobs'
          });
        }
        const clientSeq = session.nextClientSeq;
        if (!this.socket.mutate(clientSeq, intents)) return Session.connectionLost();
        return Session.gestureSent({
          clientSeq,
          gesture,
          touchedRouteUids: touchedRouteUids(gesture),
          predicted: predict(gesture, routesOnScreen(timeline.rows))
        });
      })
    )
  );

  /** One undo or redo steps one whole gesture, however many intents it sent. */
  readonly undo$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(Session.undoRequested),
        concatLatestFrom(() => [
          this.store.select(selectPlanEditSession),
          this.store.select(selectCanUndo)
        ]),
        filter(([, , can]) => can),
        map(([, session]) => this.step(session, session.cursor - 1, 'undo'))
      ),
      this.actions$.pipe(
        ofType(Session.redoRequested),
        concatLatestFrom(() => [
          this.store.select(selectPlanEditSession),
          this.store.select(selectCanRedo)
        ]),
        filter(([, , can]) => can),
        map(([, session]) => this.step(session, session.cursor, 'redo'))
      )
    )
  );

  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Session.saveRequested),
      concatLatestFrom(() => this.store.select(selectPlanEditSession)),
      filter(([, session]) => session.mode === 'editing'),
      map(([, { nextClientSeq: clientSeq }]) =>
        this.socket.save(clientSeq)
          ? Session.saveSent({ clientSeq })
          : Session.connectionLost()
      )
    )
  );

  readonly refreshOnSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Session.saved),
      concatLatestFrom(() => this.store.select(selectPlanEditSession)),
      filter(([, session]) => session.planId !== null),
      map(([{ planDataRevision }, session]) =>
        PlanEditActions.planSaved({ planId: session.planId!, planDataRevision })
      )
    )
  );

  /** The reducer ends a disconnected session locally on discard or done: nothing is sent then. */
  readonly close$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Session.discardRequested, Session.doneRequested),
      concatLatestFrom(() => this.store.select(selectPlanEditSession)),
      filter(([, session]) => session.mode !== 'off'),
      map(() =>
        this.socket.closeSession() ? Session.closeSent() : Session.connectionLost()
      )
    )
  );

  /** Only after recent activity, so DL's idle close frees the plan a tab left open holds. */
  readonly heartbeat$ = createEffect(
    () =>
      this.store.select(selectPlanEditSession).pipe(
        map((session) => session.mode === 'editing'),
        distinctUntilChanged(),
        switchMap((editing) => (editing ? interval(PLAN_EDIT_HEARTBEAT_MS) : EMPTY)),
        filter(() => Date.now() - this.activity.lastActivityAt() < PLAN_EDIT_HEARTBEAT_MS),
        tap(() => this.socket.heartbeat())
      ),
    { dispatch: false }
  );

  /**
   * The reducer leaves a session `disconnected` only when it may be resumed: after a dropped socket,
   * or a close DL parks the session for. A reconnect and a parking close can both follow one drop,
   * so a trigger while a resume awaits its answer is ignored.
   * A parking close that answers the resume is absorbed too: a resume is attempted once per drop.
   */
  readonly resume$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(ofType(PlanWatchActions.socketConnected)),
        this.actions$.pipe(ofType(Session.closed)),
        this.actions$.pipe(ofType(Session.restoredAfterReload))
      ).pipe(
        concatLatestFrom(() => this.store.select(selectPlanEditSession)),
        filter(([, s]) => s.mode === 'disconnected' && s.resumable && s.sessionId !== null),
        exhaustMap(([, s]) =>
          this.socket.resumeSession(s.sessionId!)
            ? this.actions$.pipe(
                ofType(
                  Session.resumed,
                  Session.rejected,
                  Session.closed,
                  Session.connectionLost
                ),
                take(1),
                ignoreElements()
              )
            : EMPTY
        )
      ),
    { dispatch: false }
  );

  /**
   * A resume answered after the tab ended the session locally (left while disconnected) has still
   * attached the session to this socket; nothing here would ever close it, and it would hold the
   * plan until DL's idle close.
   */
  readonly closeOrphanedResume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Session.resumed),
        concatLatestFrom(() => this.store.select(selectPlanEditSession)),
        filter(([, session]) => session.mode === 'off'),
        tap(() => this.socket.closeSession())
      ),
    { dispatch: false }
  );

  /**
   * A reload drops the socket, and DL parks the session for a resume; the page about to load needs
   * its id and what DL does not send back. A logout has already reset the session by then.
   */
  readonly stashOnPageHide$ = createEffect(
    () => {
      if (!this.window) return EMPTY;
      const stash$ = fromEvent(this.window, 'pagehide').pipe(
        concatLatestFrom(() => this.store.select(selectPlanEditSession)),
        map(([, session]) => reloadSnapshotOf(session, Date.now())),
        filter((snapshot) => snapshot !== null),
        tap((snapshot) => stashReloadSnapshot(this.sessionStorage(), snapshot))
      );
      // Back from the back-forward cache, the page still holds its session in memory: a stash left
      // behind would later resume a session it may have ended, and tell a loss that never happened.
      const restoredFromCache$ = fromEvent<PageTransitionEvent>(
        this.window,
        'pageshow'
      ).pipe(
        filter((event) => event.persisted),
        tap(() => dropReloadSnapshot(this.sessionStorage()))
      );
      return merge(stash$, restoredFromCache$);
    },
    { dispatch: false }
  );

  /** Every plan coming into view takes the stash, so it is gone once a first plan has been shown. */
  readonly restoreAfterReload$ = createEffect(() =>
    this.store.select(selectPlan).pipe(
      map((plan) => plan?.id ?? null),
      distinctUntilChanged(),
      filter((planId) => planId !== null),
      concatLatestFrom(() => this.store.select(selectPlanEditSession)),
      filter(([, session]) => session.mode === 'off'),
      map(([planId]) => takeReloadSnapshot(this.sessionStorage(), planId, Date.now())),
      filter((snapshot) => snapshot !== null),
      map((snapshot) => Session.restoredAfterReload({ snapshot }))
    )
  );

  /**
   * Every refusal and every close but the planner's own is told in plain words. A session lost with
   * no way back is told from the reducer's `lastError`, which a fresh object marks as a new loss.
   */
  readonly notify$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(Session.rejected, Session.saveRejected),
          concatLatestFrom(() => this.store.select(selectPlanEditSession)),
          tap(([{ errorCode, errorMessage, details }, session]) =>
            this.showError(
              errorCode,
              this.withSiteTimeZone(errorCode, details, session.lastError?.gesture),
              this.companyRetry(errorCode, details, session),
              errorMessage,
              session.lastError?.recompute === true
            )
          )
        ),
        this.actions$.pipe(
          ofType(Session.gestureRefusedLocally),
          tap(({ messageKey }) => this.showMessage(this.translate.instant(messageKey)))
        ),
        this.store.select(selectPlanEditSession).pipe(
          tap((s) => {
            if (s.planId !== null) this.lastEditedPlanId = s.planId;
          }),
          ignoreElements()
        ),
        this.actions$.pipe(
          ofType(Session.closed),
          filter(({ reason }) => reason !== 'CLIENT'),
          concatLatestFrom(() => [
            this.store.select(selectPlan),
            this.store.select(selectPlanEditSession)
          ]),
          // A session parked for resume is not over: a resume that fails tells so itself.
          filter(([, , session]) => session.mode !== 'disconnected'),
          tap(([{ reason }, plan]) => {
            const message = this.translate.instant(planEditMessageKey('close', reason));
            const paramId = plan?.parameter?.id;
            const editAgain =
              reason === 'IDLE_TIMEOUT' &&
              plan &&
              plan.id === this.lastEditedPlanId &&
              paramId !== undefined
                ? [
                    {
                      label: this.translate.instant(`${PLAN_EDIT_KEY}.edit_again`),
                      primary: true,
                      action: () =>
                        this.store.dispatch(
                          Session.editRequested({
                            planId: plan.id,
                            paramId
                          })
                        )
                    }
                  ]
                : undefined;
            this.showMessage(message, editAgain);
          })
        ),
        this.store.select(selectPlanEditSession).pipe(
          map((s) => s.lastError),
          distinctUntilChanged(),
          filter((error) => error?.code === CONNECTION_LOST),
          tap(() => this.showError(CONNECTION_LOST, {}))
        )
      ),
    { dispatch: false }
  );

  /**
   * Retrying a company refusal with the refused route's company cleared, in the gesture's own batch:
   * a clear sent alone is stamped back by the recompute before the gesture reaches DL.
   */
  private companyRetry(
    code: string,
    details: Record<string, unknown>,
    session: PlanEditSessionState
  ): NotificationAction[] | undefined {
    const gesture = session.lastError?.gesture;
    const routeUid = breachUids(details).route;
    if (code !== 'plan_edit_route_company_breached' || !gesture || !routeUid)
      return undefined;
    const retry = withCompanyCleared(gesture, routeUid);
    if (!retry) return undefined;
    return [
      {
        label: this.translate.instant(`${PLAN_EDIT_KEY}.clear_company_and_retry`),
        primary: true,
        action: () => this.store.dispatch(Session.gestureRequested({ gesture: retry }))
      }
    ];
  }

  /**
   * GFE's own wording where it has one. Otherwise DL's explanation, rendered as the app renders any
   * DL error, so an orchestrator or solver code still says what failed; a failed recompute says the
   * edit itself went through.
   */
  private showError(
    code: string,
    rawDetails: Record<string, unknown>,
    actions?: NotificationAction[],
    message = '',
    recompute = false
  ) {
    const reason = message ? this.reasonOf(code, message, rawDetails) : '';
    if (recompute && !reason) {
      this.showMessage(
        this.translate.instant(`${PLAN_EDIT_KEY}.errors.recompute_failed_no_reason`),
        actions
      );
      return;
    }
    if (reason && (recompute || !planEditHasOwnText(code))) {
      const key = recompute ? 'recompute_failed' : 'refused_with_reason';
      this.showMessage(
        this.translate.instant(`${PLAN_EDIT_KEY}.errors.${key}`, { reason }),
        actions
      );
      return;
    }
    const details = withKnownNames(code, rawDetails, (kind, uid) =>
      this.knownName(kind, uid)
    );
    const params = planEditMessageParams(code, details);
    if (code === 'plan_edit_quota_exceeded') {
      const key = `${PLAN_EDIT_KEY}.quota_scope.${params['scope']}`;
      const scope = this.translate.instant(key);
      params['scope'] = scope === key ? '' : scope;
    }
    this.showMessage(
      this.translate.instant(planEditMessageKey('error', code, details), params),
      actions
    );
  }

  private reasonOf(
    code: string,
    message: string,
    details: Record<string, unknown>
  ): string {
    const [view] = this.errorParser.resolve({
      errorCode: code,
      errorMessage: message,
      details
    });
    return view?.occurrences[0]?.body || message;
  }

  /**
   * A refused pin names its earliest time as an instant; the set-time form took the time in the
   * stop's site zone, so the refusal is worded in that zone too.
   */
  private withSiteTimeZone(
    code: string,
    details: Record<string, unknown>,
    gesture: PlanEditGesture | undefined
  ): Record<string, unknown> {
    if (code !== 'plan_edit_action_time_before_predecessor' || gesture?.kind !== 'pin') {
      return details;
    }
    const siteTimeZone = this.latest(selectTimelineVisualization)
      .rows.find((row) => row.uid === gesture.routeUid)
      ?.tasks.find((task) => task.actionUid === gesture.actionUid)?.siteTimeZone;
    return siteTimeZone ? { ...details, siteTimeZone } : details;
  }

  /** A name already loaded for the plan being edited; nothing is fetched. */
  private knownName(
    kind: 'route' | 'company' | 'vehicle' | 'job',
    uid: string
  ): string | undefined {
    if (kind === 'route') {
      const shown = this.latest(selectTimelineVisualization).rows.find(
        (r) => r.uid === uid
      );
      const overlay = this.latest(selectPlanWatchForView).overlay.upserted['UserRoute'] as
        | Record<string, { name?: string }>
        | undefined;
      return shown?.name ?? overlay?.[uid]?.name;
    }
    const dataSpaceId = this.latest(selectDataspaceState)?.dataspace?.id;
    if (dataSpaceId === undefined || dataSpaceId === null) return undefined;
    const planId = this.latest(selectPlan)?.id;
    return this.referenceNames.getCachedName(kind.toUpperCase(), uid, {
      dataSpaceId,
      ...(planId ? { planId } : {})
    });
  }

  private latest<T>(selector: (state: never) => T): T {
    let value!: T;
    this.store
      .select(selector as never)
      .pipe(take(1))
      .subscribe((v) => (value = v as T));
    return value;
  }

  /** Reading `sessionStorage` itself throws where site data is blocked. */
  private sessionStorage(): Storage | null {
    try {
      return this.window?.sessionStorage ?? null;
    } catch {
      return null;
    }
  }

  private showMessage(message: string, actions?: NotificationAction[]) {
    this.notifications.error(
      this.translate.instant(`${PLAN_EDIT_KEY}.notice_title`),
      message,
      actions
    );
  }

  private step(
    session: PlanEditSessionState,
    index: number,
    direction: 'undo' | 'redo'
  ): Action {
    const clientSeq = session.nextClientSeq;
    if (!this.socket[direction](clientSeq)) return Session.connectionLost();
    return Session.undoSent({
      clientSeq,
      touchedRouteUids: session.applied[index].touchedRouteUids,
      direction
    });
  }
}
